import { observer } from "mobx-react-lite";
import { useMemo, useState } from "react";

import { CustomProvidersDialog } from "./CustomProvidersDialog";
import { useCustomProvidersTable } from "./hooks/useCustomProvidersTable";

import { CustomModelDialogContent } from "@/components/CustomModels/CustomModelDialog";
import { CustomParametersStoreProvider } from "@/components/CustomModels/CustomModelsCreation/CustomParameters/custom-parameters-store";
import { DataTable, type SimpleTableColumn } from "@/components/ui/ag-grid-table";
import { useDataTableDeleteConfirm } from "@/hooks/useDataTableDeleteConfirm";
import { ModelsStoreProvider } from "@/providers/ProviderAndModelsProvider/models-store";
import { CustomProvider } from "@/queries/providers/types";

export const CustomProviders = observer(() => {
  const onRequestDeleteConfirm = useDataTableDeleteConfirm();
  const [search, setSearch] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const {
    columns,
    actions,
    isLoadingProviders,
    providers,
    customProviders,
    isEditModalOpen,
    customProvider,
    setCustomProvider,
    setIsEditModalOpen,
    setIsPreviewModalOpen,
    isPreviewModalOpen,
    contextMenuItems,
    detailCellRenderer,
    isModelDialogOpen,
    handleDelete,
    setIsModelDialogOpen,
    isConfirmationModalOpen,
    setIsConfirmationModalOpen,
  } = useCustomProvidersTable();

  const filteredProviders = useMemo(
    () =>
      customProviders?.filter((provider: CustomProvider) =>
        provider.name.toLowerCase().includes(search.toLowerCase()),
      ),
    [customProviders, search],
  );

  return (
    <div className="flex h-full flex-col">
      <DataTable<CustomProvider>
        masterDetail
        data={filteredProviders}
        onRowClick={provider => {
          if (provider.workspace_id) {
            setCustomProvider(provider);
            setIsEditModalOpen(true);
          }
        }}
        detailCellRenderer={detailCellRenderer}
        contextMenuItems={contextMenuItems}
        columns={columns as SimpleTableColumn<CustomProvider>[]}
        actions={actions}
        hasSearch
        rowsDeletable
        getDeleteDisabled={provider => !provider?.workspace_id}
        onDelete={provider => handleDelete(provider!)}
        onRequestDeleteConfirm={onRequestDeleteConfirm}
        objectName="provider"
        search={search}
        searchPlaceholder="Search custom providers"
        setSearch={setSearch}
        action={
          <div className="flex justify-between gap-2">
            <CustomProvidersDialog
              isOpen={isOpen}
              setIsOpen={setIsOpen}
              isEditModalOpen={isEditModalOpen}
              isPreviewModalOpen={isPreviewModalOpen}
              customProvider={customProvider}
              setIsEditModalOpen={setIsEditModalOpen}
              setIsPreviewModalOpen={setIsPreviewModalOpen}
              setCustomProvider={setCustomProvider}
              isLoadingProviders={isLoadingProviders}
              providers={providers}
            />
          </div>
        }
        isLoading={isLoadingProviders}
        emptyStateMessage="No custom providers found"
      />
      <ModelsStoreProvider>
        <CustomParametersStoreProvider>
          <CustomModelDialogContent
            isOpen={isModelDialogOpen}
            onOpenChange={setIsModelDialogOpen}
            setIsConfirmationModalOpen={setIsConfirmationModalOpen}
            isConfirmationModalOpen={isConfirmationModalOpen}
            defaultValues={{
              provider: customProvider?.name ?? "",
            }}
          />
        </CustomParametersStoreProvider>
      </ModelsStoreProvider>
    </div>
  );
});
